import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eraser } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "./common";
import { usePad } from "./SignatureDialog";
import { api, formatApiError } from "@/lib/api";

const Line = ({ k, v }) => (
  <p className="text-sm"><span className="text-slate-400">{k} : </span><span className="whitespace-pre-line text-white">{v || "—"}</span></p>
);

export const ContentSummary = ({ content }) => (
  <div className="space-y-1 rounded-lg border border-white/10 bg-white/[0.03] p-3" data-testid="restitution-summary">
    <Line k="Matériel" v={content.equipment} />
    <Line k="Accessoires" v={(content.accessories || []).join(", ") || "Aucun"} />
    <Line k="Diagnostic" v={content.diagnostic} />
    <Line k="Travaux réalisés" v={content.report} />
    <Line k="Pièces" v={(content.pieces || []).join(", ") || "Aucune"} />
  </div>
);

const useRestitution = (inter, resign, onDone) => {
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!inter) return;
    setName(inter.client_name || "");
    setReason("");
    api.get(`/interventions/${inter.id}/signatures/preview`, { params: { role: "restitution" } })
      .then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [inter]);
  const run = async (image) => {
    setBusy(true);
    try {
      if (image) await api.post(`/interventions/${inter.id}/signature`, { role: "restitution", name, image, replace: resign, reason: resign ? "Nouvelle signature après modification" : "" });
      if (!resign) await api.post(`/interventions/${inter.id}/workshop/stage`, { stage: "restitue", unsigned: !image, unsigned_reason: reason });
      toast.success(resign ? "Nouvelle signature enregistrée" : image ? "Matériel restitué et signé" : "Matériel restitué sans signature");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return { data, name, setName, reason, setReason, busy, run };
};

export const RestitutionDialog = ({ inter, resign = false, onClose, onSaved }) => {
  const r = useRestitution(inter, resign, () => { onClose(); onSaved?.(); });
  const pad = usePad(inter ? `${inter.id}-restitution-${resign}` : null);
  return (
    <Dialog open={!!inter} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[94vh] overflow-y-auto border-cyan-400/20 bg-[#0F1F3D] sm:max-w-xl" data-testid="restitution-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{resign ? "Nouvelle signature de restitution" : "Restitution du matériel"}</DialogTitle>
          <DialogDescription>Une seule signature, facultative : récupération du matériel et compte rendu des travaux.</DialogDescription>
        </DialogHeader>
        {r.data && <ContentSummary content={r.data.content} />}
        {r.data && <p className="rounded-lg border-l-2 border-cyan-400 bg-cyan-400/[0.06] px-3 py-2 text-sm text-cyan-100" data-testid="restitution-mention">« {r.data.mention} »</p>}
        <Field label="Nom du client"><Input value={r.name} onChange={(e) => r.setName(e.target.value)} className="h-11" data-testid="restitution-name-input" /></Field>
        <div className="relative">
          <canvas ref={pad.ref} {...pad.handlers} data-testid="restitution-canvas" className="h-48 w-full touch-none rounded-lg border-2 border-dashed border-cyan-400/40 bg-white" />
          <Button type="button" size="sm" variant="ghost" onClick={pad.clear} className="absolute right-2 top-2 text-slate-600" data-testid="restitution-clear"><Eraser className="h-4 w-4" /></Button>
        </div>
        <Button className="h-12 w-full" disabled={r.busy || pad.empty || !r.name.trim()} onClick={() => r.run(pad.ref.current.toDataURL("image/png"))} data-testid="restitution-sign-submit">
          {resign ? "Enregistrer la signature" : "Restituer avec la signature"}
        </Button>
        {!resign && (
          <div className="space-y-2 border-t border-white/10 pt-3">
            <Input value={r.reason} onChange={(e) => r.setReason(e.target.value)} placeholder="Motif de l'absence de signature (facultatif)" className="h-11" data-testid="restitution-unsigned-reason" />
            <Button variant="outline" className="h-11 w-full" disabled={r.busy} onClick={() => r.run(null)} data-testid="restitution-unsigned-submit">Restituer sans signature</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
